import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAcademicYears, useCurrentAcademicYear } from "../../hooks/useAcademicYears";

export const BURSARIES_KEY = "bursaries";

export const nameOf = (s) => [s?.firstName, s?.lastName].filter(Boolean).join(" ") || "—";

// The academic years as select options, by id (the bursary API filters by year id)
export const useYearOptions = () => {
  const { data: years = [] } = useAcademicYears();
  return years.map((y) => ({ value: y.id, label: y.academicYearName }));
};

/**
 * The academic year id in the address (so links and the back button keep it), the current year
 * until one is chosen. Returns [yearId, setYearId, options].
 */
export const useYearParam = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: current } = useCurrentAcademicYear();
  const options = useYearOptions();
  const year = searchParams.get("year") ?? "";
  useEffect(() => {
    const first = current?.id ?? options[0]?.value;
    if (!year && first) setSearchParams((params) => ({ ...Object.fromEntries(params), year: first }), { replace: true });
  }, [year, current, options, setSearchParams]);
  return [year, (value) => setSearchParams((params) => ({ ...Object.fromEntries(params), year: value })), options];
};

// The year chosen in a page's own state, the current one until then (the first listed when none is current)
export const useYearState = () => {
  const { data: current } = useCurrentAcademicYear();
  const options = useYearOptions();
  const [chosen, setYear] = useState("");
  return [chosen || current?.id || options[0]?.value || "", setYear, options];
};

// Typed search sent to the server once typing pauses; `onSettled` runs then (e.g. back to the first page)
export const useDebouncedSearch = (onSettled) => {
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounced(search.trim());
      onSettled?.();
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);
  return { search, setSearch, debounced };
};

// The legacy study areas, by name, which is what the bursary API keeps on a request
export const useStudyAreaOptions = () => {
  const { data = [] } = useQuery({
    queryKey: ["studyAreas"],
    staleTime: 30 * 60 * 1000,
    queryFn: async () => (await axios.get(`${process.env.REACT_APP_BASE_URL}hec/studyArea/getStudyAreas`)).data ?? [],
  });
  return data.map((a) => ({ value: a.studyAreaName, label: a.studyAreaName }));
};
